import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { TemplateKey, TemplateNavigationPath, Translation } from "../models/templates";
import { fetchTranslations, patchTemplate, putTemplate } from "../utils/http/templates";
import { addOuterPToTranslations } from "../utils/text";

export function usePatchTemplate(key: TemplateKey | TemplateNavigationPath, version: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchTemplate,
    onSuccess: (data) => {
      // Update the render cache with the response
      queryClient.setQueryData(["templateRender", key.namespace, key.name, version], data, {
        updatedAt: Date.now(),
      });
    },
  });
}

export function usePutTemplate(path: TemplateNavigationPath, version: string) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: putTemplate,
    onSuccess: async (data) => {
      queryClient.setQueryData(["templateRender", path.namespace, path.name, version], data);
      await queryClient.invalidateQueries({ queryKey: ["templatesSummary"] });
      await queryClient.invalidateQueries({ queryKey: ["templateRender", path.namespace, path.name, version] });
      navigate(`/templates/${path.namespace}/${path.category}/${path.name}/${version}`);
    },
  });
}

/**
 * Custom hook to fetch translations for a list of keys
 * Automatically adds outer <p> tags for TinyMCE compatibility
 *
 * @param keys - Array of translation keys to fetch
 * @param version - Template version
 * @param enabled - Whether the query should run (default: true when keys.length > 0)
 * @returns TanStack Query result with translations data
 */
export function useTranslations(keys: string[], version: string, enabled?: boolean) {
  return useQuery<{ [key: string]: Translation[] }>({
    queryKey: ["translations", keys, version] as const,
    enabled: enabled !== undefined ? enabled : keys.length > 0,
    queryFn: async ({ signal }) => {
      const translations = await fetchTranslations({ signal, keys });

      // This is a tweak for TinyMCE. It likes to wrap everything in a <p>...</p> tag, which is not what we want.
      // So we add it here when we fetch the translations from backend and strip it again when we send it back.
      // This way, users can enter text without needing to think about HTML tags, but we don't end up with
      // a lot of unnecessary <p> tags in our translations.
      for (const key in translations) {
        for (const translation of translations[key]) {
          translation.value = addOuterPToTranslations(translation.value);
        }
      }

      return translations;
    },
    staleTime: 2000,
  });
}
